/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import type { ComboboxOption } from '@opendoor/atoms/combobox';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import { Navigate } from 'react-router';

import { Card } from '../../components/Card';
import { ContentWrapper } from '../../components/ContentWrapper';
import { Loader } from '../../components/Loader';
import { LogRow, LogViewer } from '../../components/Log';
import { Table, TableHeaderCell, TableRow } from '../../components/Table';
import { apolloClient } from '../../config/apolloClients';
import { GetLogsDocument } from '../../graphql/queries/__generated__/GetLogs';
import type { GetLogs } from '../../graphql/queries/__generated__/GetLogs';

type GetLogs_getLogs_logs = GetLogs['getLogs']['logs'][number];
import { ERROR_PAGE_ROUTE } from '../routes';

const enum LogFilters {
  ALL = 'all',
  ERRORS = 'errors',
}

const LOG_FILTER_OPTIONS: ComboboxOption[] = [
  { value: LogFilters.ERRORS, label: 'Errors only' },
  { value: LogFilters.ALL, label: 'All logs' },
];

const Usage = () => {
  const [logViewerOpen, setLogViewerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<GetLogs_getLogs_logs | undefined>(undefined);
  const [fetchingMoreLogs, setFetchingMoreLogs] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>(LogFilters.ALL);

  const { loading, error, data, fetchMore, refetch } = useQuery(GetLogsDocument, {
    client: apolloClient,
    notifyOnNetworkStatusChange: true,
  });

  const handleLogViewerOpen = (log: GetLogs_getLogs_logs) => {
    setLogViewerOpen(true);
    setSelectedLog(log);
  };

  const handleLogViewerClose = () => {
    setLogViewerOpen(false);
  };

  const fetchMoreLogs = async () => {
    setFetchingMoreLogs(true);

    await fetchMore({
      variables: {
        cursor: data!.getLogs.cursor,
        errorsOnly: activeFilter === LogFilters.ERRORS,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousEntry = previousResult.getLogs;
        const newLogs = fetchMoreResult!.getLogs.logs;
        const newCursor = fetchMoreResult!.getLogs.cursor;

        return {
          getLogs: {
            cursor: newCursor,
            logs: [...previousEntry.logs, ...newLogs],
          },
        };
      },
    });

    setFetchingMoreLogs(false);
  };

  const handleRefreshClick = () => {
    refresh();
  };

  const refresh = (newFilter?: string) => {
    const filter = newFilter || activeFilter;

    switch (filter) {
      case LogFilters.ERRORS:
        refetch({ errorsOnly: true });
        break;
      case LogFilters.ALL:
      default:
        refetch({ errorsOnly: false });
    }
  };

  const handleFilterChange = (option: ComboboxOption | null) => {
    if (!option) {
      return;
    }

    setActiveFilter(option.value);
    refresh(option.value);
  };

  if (error) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  const noMoreLogs = data && data.getLogs && !data.getLogs.cursor;

  return (
    <ContentWrapper>
      <Card className="p-4">
        <Text as="h2" className="text-header-md pb-3">
          Usage Logs
        </Text>
        <Col className="pt-2 pb-3">
          <Text as="p" className="text-body-md">
            You can use the information provided here to debug and monitor your application's
            interactions with the API.
          </Text>
        </Col>
        {selectedLog && (
          <LogViewer
            isOpen={logViewerOpen}
            logData={selectedLog}
            setIsOpen={(open) => {
              if (!open) {
                handleLogViewerClose();
              }
            }}
          />
        )}
        {loading && !fetchingMoreLogs ? (
          <Loader size="small" />
        ) : (
          <>
            <Row className="w-full items-end justify-between pb-3">
              <Col className="w-45">
                <Field label="Filter" id="log-filter">
                  <Combobox
                    id="log-filter"
                    analyticsName="log-filter-dropdown"
                    value={
                      LOG_FILTER_OPTIONS.find((option) => option.value === activeFilter) ?? null
                    }
                    onValueChange={handleFilterChange}
                    options={LOG_FILTER_OPTIONS}
                  />
                </Field>
              </Col>
              <Button
                analyticsName="usage-logs-refresh"
                variant="primary"
                aria-label="Refresh usage logs"
                onClick={handleRefreshClick}
              >
                Refresh
              </Button>
            </Row>
            <Table columns="auto 1fr auto">
              <TableRow>
                <TableHeaderCell>STATUS</TableHeaderCell>
                <TableHeaderCell>DESCRIPTION</TableHeaderCell>
                <TableHeaderCell>DATE</TableHeaderCell>
              </TableRow>
              {data!.getLogs.logs.map((log) => (
                <LogRow data={log} key={log.timestamp} onClick={handleLogViewerOpen} />
              ))}
            </Table>
            {noMoreLogs ? (
              <Col className="w-full justify-center pt-2">
                <Text as="p" className="text-body-md text-od-content-secondary">
                  No more logs to display.
                </Text>
              </Col>
            ) : (
              <Col className="w-full pt-2">
                {fetchingMoreLogs && loading ? (
                  <Col className="w-full justify-center p-2">
                    <Loader size="small" />
                  </Col>
                ) : (
                  <Button
                    analyticsName="usage-logs-load-more"
                    variant="secondary"
                    className="w-full"
                    aria-label="Load more usage logs"
                    onClick={fetchMoreLogs}
                    disabled={fetchingMoreLogs && loading}
                  >
                    Load more
                  </Button>
                )}
              </Col>
            )}
          </>
        )}
      </Card>
    </ContentWrapper>
  );
};

export default Usage;
